import type { ReactNode } from 'react';
import type { BezelState } from '@/state/types';
import styles from './Monitor.module.css';

export interface MonitorProps {
  children: ReactNode;
  tokenBank?: ReactNode;
  bezelState?: BezelState;
  brand?: string;
  version?: string;
  serviceAvailable?: boolean;
  systemHalted?: boolean;
  restartAvailable?: boolean;
  onRestart?: () => void;
  networkPortVisible?: boolean;
  networkCableConnected?: boolean;
  networkControlDisabled?: boolean;
  onNetworkConnect?: () => void;
  onVersionClick?: () => void;
}

const CORNERS = ['tl', 'tr', 'bl', 'br'] as const;

export function Monitor({
  children,
  tokenBank,
  bezelState,
  brand = 'H.E.E.—H.E.E. TERMINAL',
  version = 'MK II · 0.0.1',
  serviceAvailable = false,
  systemHalted = false,
  restartAvailable = false,
  onRestart,
  networkPortVisible = false,
  networkCableConnected = false,
  networkControlDisabled = false,
  onNetworkConnect,
  onVersionClick,
}: MonitorProps) {
  return (
    <section className={styles.stage} data-testid="monitor">
      <div className={styles.chassis}>
        {CORNERS.map((c) => (
          <span key={c} aria-hidden="true" className={styles.screw} data-corner={c} />
        ))}

        <span className={styles.brandPlate} data-testid="monitor-brand-plate" aria-hidden="true">
          {brand}
        </span>

        <div className={styles.screen} data-testid="monitor-screen">
          <div className={styles.phosphor}>{children}</div>
        </div>

        {tokenBank ? (
          <div className={styles.tokenBank} data-testid="monitor-token-bank">
            {tokenBank}
          </div>
        ) : null}

        <div className={styles.statusLights} data-testid="monitor-status-lights">
          <span className={styles.statusLightGroup}>
            <span className={styles.powerLed} data-halted={systemHalted} aria-hidden="true" />
            <span>PWR</span>
          </span>
          {serviceAvailable ? (
            <>
              <span className={styles.statusLightGroup}>
                <span className={styles.hostLed} data-halted={systemHalted} aria-hidden="true" />
                <span>HOST</span>
              </span>
              <span className={styles.statusLightGroup}>
                <span
                  className={styles.serviceLed}
                  data-halted={systemHalted}
                  data-testid="monitor-service-light"
                  aria-hidden="true"
                />
                <span>SERVICE</span>
              </span>
            </>
          ) : null}
        </div>

        {networkPortVisible ? (
          <div
            className={styles.networkControl}
            data-testid="monitor-network-control"
            data-connected={networkCableConnected}
          >
            <span className={styles.networkPort} aria-hidden="true">
              <span />
            </span>
            <button
              type="button"
              className={styles.networkCable}
              data-testid="monitor-network-cable"
              aria-label={
                networkCableConnected ? 'Ethernet cable connected' : 'Connect Ethernet cable'
              }
              disabled={networkControlDisabled || networkCableConnected}
              onClick={onNetworkConnect}
            >
              <span className={styles.networkPlug} aria-hidden="true" />
              <span className={styles.networkLead} aria-hidden="true" />
            </button>
            <span className={styles.networkLabel}>NET</span>
          </div>
        ) : null}

        {restartAvailable ? (
          <button
            type="button"
            className={styles.restartControl}
            data-testid="monitor-restart-control"
            onClick={onRestart}
          >
            <span aria-hidden="true" />
            RESTART
          </button>
        ) : null}

        <button
          type="button"
          className={styles.versionLabel}
          onClick={onVersionClick}
          data-testid="version-label"
          aria-label={`Firmware ${version}`}
        >
          {version}
        </button>

        {bezelState?.cheaterNote && (
          <div className={styles.cheaterNote} data-testid="cheater-note">
            CHEATER
          </div>
        )}
      </div>
    </section>
  );
}
